// src/components/Login.js

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    IconButton,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Fade,
    Zoom,
    alpha,
    CircularProgress,
    Card,
    Alert
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Login as LoginIcon,
    Lock as LockIcon,
    Email as EmailIcon,
    School as StudentIcon,
    Work as SupervisorIcon,
    AdminPanelSettings as AdminIcon,
    MenuBook,
    Psychology,
    Send,
    ArrowBack,
    ErrorOutlineOutlined,
    AccountBalance as InstitutionHeadIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import useCustomToast from '../hooks/useToast';
import { companyInfo } from '../utils/dataInfo';
import { authService } from '../apiServices/authService';
import { motion } from 'framer-motion';
import {
    APIStudentLogin,
    APISupervisorLogin,
    APIInstitutionAdminLogin,
    APIInstitutionHeadLogin,
    APIStudentResendVerification,
    APISupervisorResendVerification,
    APIInstitutionAdminResendVerification,
    APIInstitutionHeadResendVerification
} from '../apiServices/APIs';
import axios from 'axios';

const UserTypeCard = styled(Card)(({ theme, active }) => ({
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: active ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
    backgroundColor: active ? alpha(theme.palette.primary.main, 0.05) : theme.palette.background.paper,
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[4]
    }
}));

const StyledVerificationPaper = styled(Paper)(({ theme }) => ({
    position: 'relative',
    zIndex: 3,
    width: '100%',
    maxWidth: 500,
    padding: theme.spacing(4),
    borderRadius: theme.spacing(3),
    backgroundColor: alpha(theme.palette.background.paper, 0.98),
    backdropFilter: 'blur(12px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
}));

const Login = () => {
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Flow state: 'choice' (showing Sign In / Register buttons) or 'login' (showing Login UI)
    const [viewMode, setViewMode] = useState('choice');

    const [userType, setUserType] = useState('student');
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({
        email: '',
        password: ''
    });

    // Verification modal states
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [resendEmail, setResendEmail] = useState('');
    const [resending, setResending] = useState(false);

    // User type options
    const userTypes = [
        {
            id: 'student',
            label: 'Researcher',
            icon: <StudentIcon sx={{ fontSize: 40 }} />,
            description: 'Access your research projects and supervision',
            color: '#4CAF50'
        },
        {
            id: 'supervisor',
            label: 'Research Supervisor',
            icon: <SupervisorIcon sx={{ fontSize: 40 }} />,
            description: 'Manage your supervisees and research workflow',
            color: '#2196F3'
        },
        {
            id: 'institution_admin',
            label: 'Unit Research Lead',
            icon: <AdminIcon sx={{ fontSize: 40 }} />,
            description: 'Oversee unit research activities',
            color: '#9C27B0'
        },
        {
            id: 'institution_head',
            label: 'Institutional Research Lead',
            icon: <InstitutionHeadIcon sx={{ fontSize: 40 }} />,
            description: 'Provide strategic leadership for research excellence',
            color: '#F59E0B'
        }
    ];

    // Get login endpoint based on user type
    const getLoginEndpoint = () => {
        switch (userType) {
            case 'student':
                return APIStudentLogin;
            case 'supervisor':
                return APISupervisorLogin;
            case 'institution_admin':
                return APIInstitutionAdminLogin;
            case 'institution_head':
                return APIInstitutionHeadLogin;
            default:
                return '';
        }
    };

    // Get resend verification endpoint based on user type
    const getResendVerificationEndpoint = () => {
        switch (userType) {
            case 'student':
                return APIStudentResendVerification;
            case 'supervisor':
                return APISupervisorResendVerification;
            case 'institution_admin':
                return APIInstitutionAdminResendVerification;
            case 'institution_head':
                return APIInstitutionHeadResendVerification;
            default:
                return '';
        }
    };

    const getDashboardUrl = () => {
        switch (userType) {
            case 'student':
                return '/student/dashboard';
            case 'supervisor':
                return '/supervisor/dashboard';
            case 'institution_admin':
                return '/institution-admin/dashboard';
            case 'institution_head':
                return '/institution-head/dashboard';
            default:
                return '/login';
        }
    };

    const handleUserTypeChange = (event, newUserType) => {
        if (newUserType !== null) {
            setUserType(newUserType);
            // Clear any previous errors
            setErrors({ email: '', password: '' });
            // Clear form data when switching user types
            setFormData({ email: '', password: '' });
            setShowPassword(false);
            // Close verification modal if open
            setShowVerificationModal(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = {
            email: '',
            password: ''
        };

        if (!formData.email) {
            newErrors.email = 'Email is required';
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
            valid = false;
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleResendVerification = async () => {
        if (!resendEmail) {
            showToast("Error", "Please enter your email address", "error");
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resendEmail)) {
            showToast("Error", "Please enter a valid email address", "error");
            return;
        }

        setResending(true);
        try {
            const endpoint = getResendVerificationEndpoint();
            const response = await axios.post(endpoint, {
                email: resendEmail
            });

            if (response.data.status === 'success') {
                showToast("Success", response.data.message, "success");
                // Close modal after successful resend
                setTimeout(() => {
                    setShowVerificationModal(false);
                    setResendEmail('');
                }, 2000);
            } else {
                showToast("Error", response.data.message || "Failed to resend verification", "error");
            }
        } catch (error) {
            console.error('Resend error:', error);
            showToast("Error", error.response?.data?.message || "Failed to resend verification email", "error");
        } finally {
            setResending(false);
        }
    };

    const handleLogin = async () => {
        if (!validateForm()) {
            showToast("Warning", "Please fill in all required fields", "warning");
            return;
        }

        setLoading(true);

        try {
            const endpoint = getLoginEndpoint();
            const result = await authService.login(endpoint, formData.email, formData.password, userType);

            if (result.success) {
                showToast("Success", `Welcome back, ${result.data?.name || 'User'}!`, "success");
                const url = getDashboardUrl();
                navigate(url);
            } else {
                // Check if the error is due to unverified email
                if (result.data?.needs_verification || result.message?.includes('verify your email')) {
                    setResendEmail(result.data?.email || formData.email);
                    setShowVerificationModal(true);
                    showToast("Info", "Please verify your email address first", "info");
                } else {
                    showToast("Error", result.message || "Login failed", "error");
                }
            }
        } catch (error) {
            console.error('Login error:', error);
            const errorData = error.response?.data;
            if (errorData?.data?.needs_verification || errorData?.message?.includes('verify your email')) {
                setResendEmail(errorData?.data?.email || formData.email);
                setShowVerificationModal(true);
                showToast("Info", "Please verify your email address first", "info");
            } else {
                showToast("Error", errorData?.message || "An unexpected error occurred", "error");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !loading && !showVerificationModal) {
            handleLogin();
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            position: 'relative',
            overflow: 'auto',
            alignItems: 'center',
            justifyContent: 'center',
            p: isMobile ? 2 : 3,
        }}>
            {/* Background Overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                    backgroundImage: `${theme.palette.common.auth}`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.3) 100%)',
                    }
                }}
            />

            {/* Verification Modal */}
            {showVerificationModal && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(8px)',
                    p: isMobile ? 2 : 3
                }}>
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <StyledVerificationPaper elevation={0}>
                            <Box sx={{ textAlign: 'center' }}>
                                <ErrorOutlineOutlined sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    Email Not Verified
                                </Typography>
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    Please verify your email address before logging in.
                                </Alert>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    A verification link will be sent to your email. Please check your inbox and click the verification link.
                                    <br />
                                    <br />
                                    If you didn't receive the email or the link has expired, you can request a new verification link below.
                                </Typography>

                                <Box sx={{ textAlign: 'left' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                        Enter your email address to receive a new verification link:
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        type="email"
                                        value={resendEmail}
                                        onChange={(e) => setResendEmail(e.target.value)}
                                        placeholder="your@email.com"
                                        sx={{ mb: 2 }}
                                        disabled={resending}
                                        autoFocus
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !resending) {
                                                handleResendVerification();
                                            }
                                        }}
                                    />
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleResendVerification}
                                        disabled={resending}
                                        startIcon={resending ? <CircularProgress size={20} /> : <Send />}
                                        sx={{
                                            py: 1.5,
                                            mb: 2,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {resending ? 'Sending...' : 'Send Verification Email'}
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => {
                                            setShowVerificationModal(false);
                                            setResendEmail('');
                                        }}
                                        startIcon={<ArrowBack />}
                                        sx={{
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none'
                                        }}
                                    >
                                        Back to Login
                                    </Button>
                                </Box>
                            </Box>
                        </StyledVerificationPaper>
                    </motion.div>
                </Box>
            )}

            {/* Main Container Card */}
            <Fade in={true} timeout={800}>
                <Paper
                    elevation={0}
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        width: '100%',
                        maxWidth: isMobile ? '95%' : 550,
                        p: isMobile ? 2 : { xs: 3, sm: 4 },
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.background.paper, 0.92),
                        backdropFilter: 'blur(12px)',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}
                >
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        {/* Header */}
                        <Zoom in={true} timeout={500}>
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mb: 2,
                                        position: 'relative'
                                    }}>
                                        <Box sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: isMobile ? 100 : 120,
                                            height: isMobile ? 100 : 120,
                                            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.2)} 0%, transparent 70%)`,
                                            borderRadius: '50%',
                                            filter: 'blur(20px)',
                                            zIndex: 0
                                        }} />
                                        <img
                                            src={companyInfo.logo}
                                            alt={`${companyInfo.name} Logo`}
                                            style={{
                                                height: isMobile ? '60px' : '80px',
                                                width: 'auto',
                                                maxWidth: '100%',
                                                objectFit: 'contain',
                                                position: 'relative',
                                                zIndex: 1,
                                                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))'
                                            }}
                                        />
                                    </Box>
                                </motion.div>

                                <motion.div
                                    initial={{ y: -20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.1 }}
                                >
                                    <Typography
                                        variant={isMobile ? 'h5' : 'h4'}
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 1,
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            letterSpacing: '-0.5px'
                                        }}
                                    >
                                        {viewMode === 'choice' ? 'SJC ScholarFlow' : 'Sign In to ScholarFlow'}
                                    </Typography>
                                </motion.div>

                                <motion.div
                                    initial={{ y: -20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: 'text.secondary',
                                            mt: 1,
                                            fontWeight: 500,
                                            fontSize: isMobile ? '0.85rem' : '0.95rem'
                                        }}
                                    >
                                        {viewMode === 'choice'
                                            ? 'Research Workflow & Supervision Platform'
                                            : 'Sign in to your account'}
                                    </Typography>
                                </motion.div>
                            </Box>
                        </Zoom>

                        {/* Choice Mode UI */}
                        {viewMode === 'choice' ? (
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 4, mb: 2 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => setViewMode('login')}
                                        startIcon={<LoginIcon />}
                                        sx={{
                                            py: isMobile ? 1.5 : 2,
                                            fontSize: isMobile ? '0.95rem' : '1.05rem',
                                            borderRadius: 2.5,
                                            fontWeight: 'bold',
                                            textTransform: 'none',
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        Sign In
                                    </Button>

                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => navigate('/register')}
                                        startIcon={<MenuBook />}
                                        sx={{
                                            py: isMobile ? 1.5 : 2,
                                            fontSize: isMobile ? '0.95rem' : '1.05rem',
                                            borderRadius: 2.5,
                                            fontWeight: 'bold',
                                            textTransform: 'none',
                                            borderWidth: '1.5px',
                                            '&:hover': {
                                                borderWidth: '1.5px',
                                                transform: 'translateY(-2px)',
                                                backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        Register
                                    </Button>
                                </Box>
                            </motion.div>
                        ) : (
                            /* Credentials Entry UI */
                            <>
                                {/* User Type Selection - Now with 4 cards (2x2 on mobile, 4x1 on desktop) */}
                                <motion.div
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.1 }}
                                >
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
                                        Select Account Type
                                    </Typography>
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                                        gap: 1.5,
                                        mb: 3
                                    }}>
                                        {userTypes.map((type) => (
                                            <UserTypeCard
                                                key={type.id}
                                                active={userType === type.id}
                                                onClick={() => handleUserTypeChange(null, type.id)}
                                                sx={{
                                                    p: 1.5,
                                                    textAlign: 'center',
                                                    borderColor: userType === type.id ? type.color : 'divider',
                                                    borderWidth: userType === type.id ? 2 : 1,
                                                    '&:hover': {
                                                        borderColor: type.color,
                                                    }
                                                }}
                                            >
                                                <Box sx={{ color: type.color, mb: 0.5 }}>
                                                    {type.icon}
                                                </Box>
                                                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                                                    {type.label}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{
                                                    display: 'block',
                                                    fontSize: isMobile ? '0.5rem' : '0.6rem',
                                                    lineHeight: 1.2,
                                                    mt: 0.25
                                                }}>
                                                    {type.description.split(' ').slice(0, 3).join(' ')}...
                                                </Typography>
                                            </UserTypeCard>
                                        ))}
                                    </Box>
                                </motion.div>

                                {/* Login Form */}
                                <motion.div
                                    initial={{ y: 30, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                >
                                    <Box component="form" sx={{ mt: 2 }} onKeyPress={handleKeyPress}>
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            error={!!errors.email}
                                            helperText={errors.email}
                                            margin="normal"
                                            disabled={loading}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EmailIcon sx={{ color: theme.palette.primary.main }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                mb: 2,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    backgroundColor: alpha(theme.palette.common.white, 0.7),
                                                    backdropFilter: 'blur(4px)',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.common.white, 0.9),
                                                    },
                                                    '&.Mui-focused': {
                                                        backgroundColor: alpha(theme.palette.common.white, 0.95),
                                                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                                                    }
                                                }
                                            }}
                                            size={isMobile ? 'small' : 'medium'}
                                        />

                                        <TextField
                                            fullWidth
                                            label="Password"
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={handleChange}
                                            error={!!errors.password}
                                            helperText={errors.password}
                                            margin="normal"
                                            disabled={loading}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockIcon sx={{ color: theme.palette.primary.main }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            type="button"
                                                            onClick={togglePasswordVisibility}
                                                            edge="end"
                                                            size={isMobile ? 'small' : 'medium'}
                                                            disabled={loading}
                                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                mb: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    backgroundColor: alpha(theme.palette.common.white, 0.7),
                                                    backdropFilter: 'blur(4px)',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.common.white, 0.9),
                                                    },
                                                    '&.Mui-focused': {
                                                        backgroundColor: alpha(theme.palette.common.white, 0.95),
                                                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                                                    }
                                                }
                                            }}
                                            size={isMobile ? 'small' : 'medium'}
                                        />

                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            mb: 3,
                                            mt: 1
                                        }}>
                                            {userType === "student" && (
                                                <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            fontWeight: 600,
                                                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                            '&:hover': { textDecoration: 'underline' }
                                                        }}
                                                    >
                                                        Forgot password?
                                                    </Typography>
                                                </Link>
                                            )}
                                            {userType === "supervisor" && (
                                                <Link to="/supervisor/forgot-password" style={{ textDecoration: 'none' }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            fontWeight: 600,
                                                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                            '&:hover': { textDecoration: 'underline' }
                                                        }}
                                                    >
                                                        Forgot password?
                                                    </Typography>
                                                </Link>
                                            )}
                                            {userType === "institution_admin" && (
                                                <Link to="/institution-admin/forgot-password" style={{ textDecoration: 'none' }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            fontWeight: 600,
                                                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                            '&:hover': { textDecoration: 'underline' }
                                                        }}
                                                    >
                                                        Forgot password?
                                                    </Typography>
                                                </Link>
                                            )}
                                            {userType === "institution_head" && (
                                                <Link to="/institution-head/forgot-password" style={{ textDecoration: 'none' }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: theme.palette.warning.main,
                                                            fontWeight: 600,
                                                            fontSize: isMobile ? '0.7rem' : '0.875rem',
                                                            '&:hover': { textDecoration: 'underline' }
                                                        }}
                                                    >
                                                        Forgot password?
                                                    </Typography>
                                                </Link>
                                            )}
                                        </Box>

                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            onClick={handleLogin}
                                            disabled={loading}
                                            startIcon={!loading && <LoginIcon />}
                                            sx={{
                                                py: isMobile ? 1.2 : 1.5,
                                                mb: 2,
                                                fontSize: isMobile ? '0.9rem' : '1rem',
                                                borderRadius: 2,
                                                fontWeight: 'bold',
                                                textTransform: 'none',
                                                background: userType === 'institution_head'
                                                    ? `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`
                                                    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: userType === 'institution_head'
                                                        ? `0 8px 25px ${alpha(theme.palette.warning.main, 0.4)}`
                                                        : `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`
                                                },
                                                '&:active': {
                                                    transform: 'translateY(0)'
                                                },
                                                transition: 'all 0.2s ease'
                                            }}
                                            size={isMobile ? 'medium' : 'large'}
                                        >
                                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                                        </Button>

                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mt: 2,
                                            pt: 2,
                                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                                        }}>
                                            <Button
                                                variant="text"
                                                onClick={() => setViewMode('choice')}
                                                startIcon={<ArrowBack />}
                                                disabled={loading}
                                                sx={{
                                                    textTransform: 'none',
                                                    color: 'text.secondary',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                Back
                                            </Button>

                                            <Button
                                                component={Link}
                                                to="/register"
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<MenuBook />}
                                                sx={{
                                                    textTransform: 'none',
                                                    borderRadius: 2,
                                                    '&:hover': { transform: 'translateY(-2px)' },
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                Register Now
                                            </Button>
                                        </Box>

                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            mt: 3,
                                            pt: 1
                                        }}>
                                            <Psychology sx={{ fontSize: 14, mr: 0.5, color: theme.palette.primary.main }} />
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: isMobile ? '0.65rem' : '0.7rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                Research Workflow & Supervision Platform
                                            </Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            </>
                        )}
                    </Box>
                </Paper>
            </Fade>
        </Box>
    );
};

export default Login;